// Shared display helpers for backend-computed booking window status / decision and for the
// booking customer-information fields (v2 Item 6). Used by createTicketDialog,
// bookingDetailDialog and currentQueueCard so the vocabulary lives in one place.
//
// NOTE ON PRIVACY: `tel` / `email` / `policy_number` are rendered exactly as the backend
// returns them. The backend owns masking (MASK_CONTACT_INFO); this panel must never mask
// or un-mask them itself, otherwise the two layers would disagree about what staff may see.
const HHMM = /^(\d{1,2}:\d{2})(?::\d{2})?$/

// '14:30:00' -> '14:30'; anything that is not a plain time string is returned trimmed but intact.
const to_hhmm = value => {
  if (!value) return null
  const text = String(value).trim()
  const match = text.match(HHMM)
  return match ? match[1] : text
}

export default {
  methods: {
    windowStatusLabel(status) {
      switch (status) {
        case 'within_window':
          return 'Booking'
        case 'early':
        case 'late':
          return 'Walk-in'
        default:
          return 'N/A'
      }
    },
    windowStatusColor(status) {
      switch (status) {
        case 'within_window':
          return 'green'
        case 'early':
        case 'late':
          return 'orange'
        default:
          return 'grey'
      }
    },
    // A booking that has already produced a ticket. Same rule as the backend's create-time guard
    // ("This booking already has a created ticket": `ticket_id` linked, or the legacy
    // `is_created_ticket` flag) and the kiosk's already-ticketed check, so all three layers agree.
    // The flag is compared numerically: some MySQL drivers return TINYINT as the string '1'.
    isBookingTicketed(booking) {
      if (!booking) return false
      return booking.ticket_id != null || Number(booking.is_created_ticket) === 1
    },
    // Card marker for a ticketed booking. `ticket_code` is the joined `tickets.code`; it is absent
    // on a backend that does not join it yet, or when the ticket row was since removed — the
    // booking is still used up either way, so the marker never disappears with the code.
    bookingTicketLabel(booking) {
      if (!this.isBookingTicketed(booking)) return null
      return booking.ticket_code ? `Ticket issued: ${booking.ticket_code}` : 'Ticket issued'
    },
    ticketDecisionLabel(decision) {
      if (decision === 'booking') return 'Booking ticket'
      if (decision === 'walkin') return 'Walk-in ticket'
      return 'N/A'
    },
    // A queued ticket belongs to an online booking when the backend linked it to a booking
    // record — either through the ticket's own `booking_id` or the nested `booking` object it
    // attaches to the queue payload. Walk-in tickets have neither.
    getTicketBookingID(ticket) {
      if (!ticket) return null
      return ticket.booking_id || (ticket.booking && ticket.booking.booking_id) || null
    },
    // Booked Timeslot, e.g. "14:30 - 15:00". The backend computes the end time (start + 30 min)
    // and ships a ready-made `booking_timeslot`; the start/end fallback only covers a backend
    // that has not been upgraded yet. The end time is never derived here.
    bookingTimeslot(booking) {
      if (!booking) return 'N/A'
      if (booking.booking_timeslot) return booking.booking_timeslot
      const start = to_hhmm(booking.time)
      if (!start) return 'N/A'
      const end = to_hhmm(booking.booking_end_time)
      return end ? `${start} - ${end}` : start
    },
    // Contact number = Area Code + Phone Number, both taken verbatim from the backend.
    bookingContactNumber(booking) {
      if (!booking) return 'N/A'
      const parts = [booking.area_code, booking.tel].filter(part => part != null && String(part).trim() !== '')
      return parts.length ? parts.join(' ') : 'N/A'
    },
    formatDateTime(value, format = 'YYYY-MM-DD HH:mm') {
      if (!value) return 'N/A'
      return this.$moment(value).format(format)
    },
    getStaffNameByID(user_id) {
      const user = this.general.users.find(user => user.user_id === user_id)
      return user ? `${user.firstname} ${user.lastname}` : 'N/A'
    }
  }
}
